import { Box, IconButton, Link, Tooltip, useTheme } from '@mui/material';
import { useState } from 'react';
import { tokens } from '../../global/theme/tokens';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ExoTable from '../../components/exo/ExoTable';
import { OfferStatus } from '../../components/special/Status';
import ExtendOffer from './ExtendOffer';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import UploadSigendOffer from './UploadSignedOffer';
import Header from '../../components/special/Header';
import ExoTableRecipient from '../../components/exo/ExoTableRecipient';
import NewOffer from '../../components/new/NewOffer';
import { useTranslation } from 'react-i18next';
import MoneyLabel from '../../components/label/MoneyLabel';
import useFetch from '../../api/useFetch';
import { useNavigate } from 'react-router-dom';
import { recipientTypeMap } from '../../components/routing/routingMaps';
import DateLabel from '../../components/label/DateLabel';
import { LeadStatusSelector } from '../leads/status/LeadStatusSelector';
import { API_PATCH, DBRequest, useConfig } from '../../api/api';
import { updateOrCreate } from '../../components/special/updateOrCreate';
import Feedback from '../../components/special/Feedback';
import ExoAlert from '../../components/exo/ExoAlert';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import DocumentOverviewCard from '../../components/document-overview/DocumentOverviewCard';
import { buildPermanentFilter } from '../../components/document-overview/buildPermanentFilter';

const Offers = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const navigate = useNavigate();

  // null until the overview card reports its (persisted) filter — prevents a
  // double table fetch (unfiltered then filtered) on initial load.
  const [overviewFilters, setOverviewFilters] = useState(null);
  const permanentFilter = overviewFilters
    ? buildPermanentFilter({ ...overviewFilters, dateField: 'date' })
    : '';
  const fetchPath =
    overviewFilters === null
      ? null
      : permanentFilter
        ? `offers?${permanentFilter}`
        : 'offers';

  const { apiData, setApiData } = useFetch(fetchPath);

  const [status, setStatus] = useState(null);

  const [alertOpen, setAlertOpen] = useState(null);
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToChange, setItemToChange] = useState(null);
  const handleStatusClick = (event, item) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setItemToChange(item);
  };
  function handleStatusChange(status, item) {
    handleChange(status.status, item);
  }

  function handleChange(newStatus, row) {
    setStatus(newStatus);
    if (newStatus === 1) {
      setAlertOpen(row);
    } else {
      requestStatusChange(newStatus, false, row);
    }
  }

  function requestStatusChange(reqStatus, sendOrderConfirmation, row) {
    setAlertOpen(null);
    const reqData = {
      status: reqStatus,
      sendOrderConfirmation,
    };

    DBRequest({
      config,
      path: `offers/${row.id}/status`,
      method: API_PATCH(reqData),
      onFeedback: handleAlertOpen,
      onResponse: () => {
        setApiData({
          ...apiData,
          data: updateOrCreate(apiData.data, { ...row, status: reqStatus }),
        });
        //onUpdate({ ...row, status: reqStatus });
      },
    });
  }

  // table
  const Columns = [
    {
      header: t('Nr'), // header of column
      key: 'nr', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <Box className="monospace whitespace-nowrap">
          {row.nr ?? t('Draft')}
        </Box>
      ), // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Status'),
      key: 'status',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <OfferStatus
          number={row.status}
          onClick={(e) => handleStatusClick(e, row)}
        ></OfferStatus>
      ), // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Date'),
      key: 'date',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      hiddenMobile: true,
      cell: ({ row }) => <DateLabel date={row.date}></DateLabel>,
    },
    {
      header: t('Title'),
      key: 'title',
      width: '100%',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      hiddenMobile: true,
      onClick: (row) => navigate(`/offers/${row.id}`),
    },
    {
      header: t('Project'),
      key: 'project',
      thAlign: 'left',
      tdAlign: 'left',
      hiddenMobile: true,
      cell: ({ row }) => (
        <Box className="whitespace-nowrap">
          {row.project ? `${row.project.nr ?? ''} ${row.project.name}`.trim() : ''}
        </Box>
      ),
      onClick: (row) => row.project && navigate(`/projects/${row.project.id}`),
    },
    {
      header: t('Client'),
      key: 'recipient',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      className: 'w-full md:min-w-[270px]',
      cell: ({ row }) => <ExoTableRecipient row={row} />, // custom xml for cells in the column. you get the row as object
      onClick: (row) =>
        navigate(`/${recipientTypeMap[row.recipientType]}/${row.recipient.id}`),
    },
    {
      header: t('netto'),
      key: 'nettoVolume',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.volume} />,
      sortable: true, // enable sort function for column
    },
    {
      header: t('tax'),
      key: 'taxVolume',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.taxVolume} />,
      sortable: true, // enable sort function for column
    },
    {
      header: t('brutto'),
      key: 'volume',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <MoneyLabel money={row.taxVolume + row.volume} />,
      sortable: true, // enable sort function for column
    },
    {
      header: t('skonto'),
      key: 'skonto',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      className: 'w-full lg:w-auto',
      cell: ({ row }) => {
        const brutto = row.taxVolume + row.volume;
        return (
          <MoneyLabel
            money={
              row.discount
                ? brutto - brutto * (parseFloat(row.discountRate) / 100)
                : brutto
            }
          />
        );
      },
      sortable: true, // enable sort function for column
    },
    {
      header: t('Valid Until'),
      key: 'validUntil',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      cell: ({ row }) => <DateLabel date={row.validUntil} />,
      sortable: true, // enable sort function for column
    },
    {
      header: '',
      key: 'link',
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      className: 'w-full lg:w-auto',
      cell: ({ row }) => (
        <Tooltip title={t('Open in new window')}>
          <Link href={`/offers/${row.id}`} target="_blank">
            <IconButton>
              <OpenInBrowserIcon />
            </IconButton>
          </Link>
        </Tooltip>
      ),
    },
  ];

  const filters = {
    filter: {
      label: t('Filter'),
      items: [
        {
          type: 'dropdown',
          label: t('Status'),
          key: 'state2',
          baseValue: 'status[eq]=',
          active: false,
          options: [
            {
              label: t('Sent'),
              value: 0,
            },
            {
              label: t('Accepted'),
              value: 1,
            },
            {
              label: t('Rejected'),
              value: 3,
            },
            {
              label: t('Created'),
              value: 4,
            },
          ],
        },
      ],
    },
  };

  // new offer
  const [openNew, setOpenNew] = useState(false);
  const handleClickNewOffer = (event, reason) => {
    setOpenNew(!openNew);
  };

  // upload singed offer
  const [openUploadSignedOffer, setOpenUploadSignedOffer] = useState(false);

  return (
    <>
      <Header title={t('Offers')} />

      <DocumentOverviewCard
        resource="offers"
        preferencesKey="offers"
        onFilterChange={setOverviewFilters}
        actions={[
          {
            primary: true,
            label: t('New Offer'),
            icon: <NoteAddIcon fontSize="small" />,
            onClick: handleClickNewOffer,
          },
          {
            label: t('Upload Signed Offer'),
            icon: <UploadFileIcon fontSize="small" />,
            onClick: () => setOpenUploadSignedOffer(true),
          },
        ]}
      />

      <ExoTable
        data={apiData}
        isLoading={!Boolean(apiData)}
        columns={Columns}
        extendElement={({ row, onUpdate, onDelete }) => (
          <ExtendOffer row={row} onUpdate={onUpdate} onDelete={onDelete} />
        )}
        filter={permanentFilter}
        filters={filters}
        columnSettingsKey="offers"
        defaultHiddenColumns={[
          'taxVolume',
          'volume',
          'validUntil',
          'skonto',
          'project',
        ]}
      />
      <NewOffer
        offers={apiData}
        setOffers={setApiData}
        onClose={() => setOpenNew(false)}
        open={openNew}
      />
      <UploadSigendOffer
        open={openUploadSignedOffer}
        onClose={() => setOpenUploadSignedOffer(false)}
      />
      <LeadStatusSelector
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onStatusChange={(status) => handleStatusChange(status, itemToChange)}
        customStatuses={[
          {
            label: t('Created'),
            status: 4,
            variant: t('open'),
          },
          {
            label: t('Sent'),
            status: 0,
            variant: t('sent'),
          },
          {
            label: t('Accepted'),
            status: 1,
            variant: t('accepted'),
          },
          {
            label: t('Rejected'),
            status: 3,
            variant: t('rejected'),
          },
        ]}
      />
      <ExoAlert
        open={alertOpen}
        alert={{
          message: t(
            'Should an order confirmation be sent to the customer by e-mail?',
          ),
          type: 'info',
          submitLabel: t('yes'),
          cancelLabel: t('no'),
        }}
        onSubmit={() => requestStatusChange(status, true, alertOpen)}
        onClose={() => requestStatusChange(status, false, alertOpen)}
      />
      <Feedback setState={setAlertState} state={alertState} />{' '}
    </>
  );
};

export default Offers;
